import { Injectable, Optional } from '@angular/core';
import { KNOWN_COUNTRIES } from './known-countries';
import { LoggerService } from '@core/services/logger/logger.service';
import { RxAppBff } from '../models/environment-params';
import { Maybe } from '@/types/globals';
import { AWS_REGIONS } from '@shared/utils/aws_regions';

const COUNTRY_TO_CLUSTER: Record<string, string> = (() => {
	const map: Record<string, string> = {};

	Object.keys(KNOWN_COUNTRIES).forEach(cluster => {
		KNOWN_COUNTRIES[cluster].forEach(code => {
			map[code] = cluster;
		});
	});

	return map;
})();

@Injectable({
	providedIn: 'root'
})
export class RegionClusterMapper {
	constructor(@Optional() private logger: LoggerService | null) {}

	map(region: Maybe<string>): string | null {
		if (!region) {
			this.logWarn('[RegionClusterMapper]Region is not provided');

			return null;
		}

		const normalized = region.trim().toLowerCase();
		const cluster = this.isAwsRegion(normalized) ? this.mapAwsRegion(normalized) : COUNTRY_TO_CLUSTER[normalized];

		if (!cluster) {
			this.logWarn(`[RegionClusterMapper] Unknown region "${region}".`);

			return null;
		}

		return cluster;
	}

	resolveBffUrl(appSettings: { rxappBff?: RxAppBff }, region: Maybe<string>): string | null {
		if (!appSettings) {
			return null;
		}

		const cluster = this.map(region);

		if (!cluster) {
			return null;
		}

		const container = appSettings.rxappBff;

		if (!container) {
			return null;
		}

		const urls = container.urls;
		const resolvedUrl = urls?.[cluster as keyof RxAppBff['urls']];

		if (!resolvedUrl) {
			this.logWarn(`[RegionClusterMapper] Missing BFF url for cluster '${cluster}'.`);

			return null;
		}
		this.logInfo(`[RegionClusterMapper] Resolved BFF url for cluster '${cluster}': ${resolvedUrl}`);

		return resolvedUrl;
	}

	private logWarn(message: string): void {
		if (this.logger) {
			this.logger.warn(message, { module: 'RegionClusterMapper' });
		}
	}

	private logInfo(message: string): void {
		if (this.logger) {
			this.logger.info(message, { module: 'RegionClusterMapper' });
		}
	}

	private isAwsRegion(region: string): boolean {
		return AWS_REGIONS.includes(region);
	}

	private mapAwsRegion(region: string): string {
		return region.split('-')[0];
	}
}
